import enCommon from '@/i18n/locales/en/common.json';

export interface OptionItem<T extends string = string> {
  id: string;
  value: T;
  i18nKey: string;
}

export const YES = getEn('forms.shared.yesNo.yes');
export const NO = getEn('forms.shared.yesNo.no');
export const YES_NO_MAP = createMap([
  'forms.shared.yesNo.yes',
  'forms.shared.yesNo.no',
] as const);
export const YES_NO = [YES, NO] as const;

export const BUILDING_TYPES_MAP = createMap([
  'forms.fiberInterest.buildingTypes.commercial',
  'forms.fiberInterest.buildingTypes.communityConnection',
  'forms.fiberInterest.buildingTypes.mixedUse',
  'forms.fiberInterest.buildingTypes.multifamily',
  'forms.fiberInterest.buildingTypes.singleFamily',
  'forms.fiberInterest.buildingTypes.other',
] as const);
export const BUILDING_TYPES = BUILDING_TYPES_MAP.map(item => item.value) as [
  string,
  ...string[],
];

export const PROPERTY_RELATIONSHIPS_MAP = createMap([
  'forms.fiberInterest.propertyRelationships.propertyOwner',
  'forms.fiberInterest.propertyRelationships.propertyManager',
  'forms.fiberInterest.propertyRelationships.leasingCompany',
  'forms.fiberInterest.propertyRelationships.hoa',
  'forms.fiberInterest.propertyRelationships.other',
] as const);
export const PROPERTY_RELATIONSHIPS = PROPERTY_RELATIONSHIPS_MAP.map(
  item => item.value,
) as [string, ...string[]];

export const QUESTION_TYPES_MAP = createMap([
  'forms.contactBilling.questionTypes.creditRefundRequest',
  'forms.contactBilling.questionTypes.outstandingBalance',
  'forms.contactBilling.questionTypes.billingStatement',
  'forms.contactBilling.questionTypes.delinquencySuspension',
  'forms.contactBilling.questionTypes.other',
] as const);
export const QUESTION_TYPES = QUESTION_TYPES_MAP.map(item => item.value) as [
  string,
  ...string[],
];

export const PROPERTY_TYPES_MAP = createMap([
  'forms.contactSales.propertyTypes.residential',
  'forms.contactSales.propertyTypes.smallBusiness',
] as const);
export const PROPERTY_TYPES = PROPERTY_TYPES_MAP.map(item => item.value) as [
  string,
  ...string[],
];

export const REQUEST_TYPES_MAP = createMap([
  'forms.dataRequest.requestTypes.access',
  'forms.dataRequest.requestTypes.deletion',
  'forms.dataRequest.requestTypes.correction',
] as const);
export const REQUEST_TYPES = REQUEST_TYPES_MAP.map(item => item.value) as [
  string,
  ...string[],
];

export const RESPONSE_TYPES_MAP = createMap([
  'forms.dataRequest.responseTypes.electronic',
  'forms.dataRequest.responseTypes.mail',
] as const);
export const RESPONSE_TYPES = RESPONSE_TYPES_MAP.map(item => item.value) as [
  string,
  ...string[],
];

export const RELATIONSHIPS_MAP = createMap([
  'forms.dataRequest.relationships.individual',
  'forms.dataRequest.relationships.agent',
] as const);
export const RELATIONSHIPS = RELATIONSHIPS_MAP.map(item => item.value) as [
  string,
  ...string[],
];

export const STATUS_OPTIONS_MAP = createMap([
  'forms.fiberDeceased.statusOptions.deceased',
  'forms.fiberDeceased.statusOptions.incapacitated',
] as const);
export const STATUS_OPTIONS = STATUS_OPTIONS_MAP.map(item => item.value) as [
  string,
  ...string[],
];

export const ACTION_OPTIONS_MAP = createMap([
  'forms.fiberDeceased.actionOptions.transfer',
  'forms.fiberDeceased.actionOptions.cancel',
] as const);
export const ACTION_OPTIONS = ACTION_OPTIONS_MAP.map(item => item.value) as [
  string,
  ...string[],
];

export const I_AM_MAP = createMap([
  'forms.fiberInterestCityOfficial.iam.governmentOfficial',
  'forms.fiberInterestCityOfficial.iam.utilityOfficial',
] as const);
export const I_AM = I_AM_MAP.map(item => item.value) as [string, ...string[]];

export const LEGAL_STATUS = {
  DECEASED: getEn('forms.fiberDeceased.statusOptions.deceased'),
  INCAPACITATED: getEn('forms.fiberDeceased.statusOptions.incapacitated'),
} as const;

export const ACCOUNT_ACTION = {
  TRANSFER: getEn('forms.fiberDeceased.actionOptions.transfer'),
  CANCEL: getEn('forms.fiberDeceased.actionOptions.cancel'),
} as const;

function getEn(key: string): string {
  let curr: unknown = enCommon;
  for (const part of key.split('.')) {
    if (
      typeof curr === 'object' &&
      curr !== null &&
      Object.hasOwn(curr, part)
    ) {
      curr = (curr as Record<string, unknown>)[part];
    } else {
      throw new Error(`Translation key "${key}" not found in enCommon`);
    }
  }
  if (typeof curr !== 'string') {
    throw new Error(
      `Translation key "${key}" does not resolve to a string in enCommon`,
    );
  }
  return curr;
}

function createMap<K extends `${string}.${string}`>(
  i18nKeys: readonly K[],
): OptionItem[] {
  return i18nKeys.map(i18nKey => ({
    id: i18nKey.split('.').pop() as string,
    value: getEn(i18nKey),
    i18nKey,
  }));
}
